/**
 * Captcha React component.
 */

// Core lib dependencies.
import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

// Config.
import Config from '../config/config.js';

// Legacy dependencies.
import { requireSDK } from '../../../../legacy/utils/utils.js';
import Enums from '../../Voting/config/enums.js';
import enums from '../../../../common/config/enums.js';

/**
 * <pre>
 * Captcha module React class.
 *
 * This component outputs markup to render a Captcha component.
 *
 * Privacy Management Note: reCaptcha creates a cookie _GRECAPTCHA, which is used for fraud
 * prevention.  It is considered strictly necessary by reCaptcha, and therefore also by us.
 * It doesn't need gating.
 *
 * </pre>
 *
 * @author Tim Loram
 *
 * @docPreview {namedExport: "docPreviewExport"}
 *
 * @memberof module:Captcha.Components
 * @namespace
 *
 * @return {module:Captcha.Components.Captcha}
 */
class Captcha extends React.Component {
  // By default, we use the Google Recaptcha lib (to ensure the backward compatibility)
  captchaSettings = enums.CAPTCHA_LIBS.recaptcha;

  /**
   * Constructor.
   *
   * @param {object} props The injected props
   */
  constructor(props) {
    super(props);

    // Bind methods.
    this.handleCaptchaResponse = this.handleCaptchaResponse.bind(this);
    this.captchaContainerRef = React.createRef(null);
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Lifecycle methods */

  /**
   * Called when component mounts for the first time.
   * Loads and initialises the Captcha SDK.
   */
  componentDidMount() {
    switch (this.props.fraudStrategy) {
      case Enums.STRATEGY_FRAUD_CAPTCHA:
      case Enums.STRATEGY_FRAUD_DYNCAPTCHA: {
        this.captchaSettings = enums.CAPTCHA_LIBS.recaptcha;
        break;
      }
      case Enums.STRATEGY_FRAUD_TURNSTILE:
      case Enums.STRATEGY_FRAUD_DYNCAPTCHA_TURNSTILE: {
        this.captchaSettings = enums.CAPTCHA_LIBS.turnstile;
        break;
      }
    }
    // Load SDK.
    requireSDK(this.captchaSettings.sdk, () => {
      // TODO: [SG] Fix this race condition
      setTimeout(() => {
        try {
          if (this.captchaContainerRef.current) {
            window[this.captchaSettings.libName].render(this.captchaContainerRef.current, {
              sitekey:  NGXD.getServiceContainer().getService('config').get(`keys.${this.captchaSettings.sdk}`),
              theme:    'light',
              callback: this.handleCaptchaResponse,
            });
            document.body.classList.add('xCaptchaContainer');
          }
        } catch (e) {}
      }, 500);
    });
  }

  componentWillUnmount() {
    // cleanup
    document.body.classList.remove('xCaptchaContainer');
    if (
      !!window[this.captchaSettings.libName] &&
      window[this.captchaSettings.libName][this.captchaSettings.resetMethod]
    ) {
      // An attempt to supress the error which can happen if user starts quickly clicking on the button
      // which triggers the captcha rendering. This case, the captcha overlay mounts/unmounts quickly,
      // and some errors occur (no reCAPTCHA element, etc.). To avoid it, this ugly hack was added.
      try {
        window[this.captchaSettings.libName][this.captchaSettings.resetMethod]();
      } catch (error) {}
    }
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Render methods */

  /**
   * Render the component. Compose child components as required.
   *
   * @return {module:Captcha.Components.Captcha}
   */
  render() {
    return (
      <form id="xFormRecaptcha" method="post">
        <div ref={this.captchaContainerRef} id="recaptchaWrapper"></div>
      </form>
    );
  }

  /* - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - */
  /* Event handler methods */

  /**
   * Handler called when captcha has been completed.
   *
   * @param {Object} response The Captcha response object.
   */
  handleCaptchaResponse(response) {
    // Allow enough time for the grecaptcha animation to finish playing before removing it from the DOM, otherwise it has a hissy fit.
    // There is no event or callback to notify us of when this finishes. Great library :/
    setTimeout(() => {
      // Call supplied handler, passing the response.
      if (this.props.closeHandler) {
        this.props.closeHandler();
      }
      if (this.props.responseHandler) {
        this.props.responseHandler(response);
      }
    }, 2000);
  }
}

/**
 * React PropTypes.
 *
 * @type {Object}
 * @property {Function} responseHandler The handler to call when Captcha response is received.
 */
Captcha.propTypes = {
  responseHandler: PropTypes.func,
  closeHandler:    PropTypes.func,
  fraudStrategy:   PropTypes.string,
};

/**
 * React default props.
 *
 * @type {Object}
 */
Captcha.defaultProps = {};

/**
 * Maps Redux state values to React component props. Called automatically whenever
 * the bound state changes.
 *
 * @private
 *
 * @param  {Object} state The Redux state.
 * @param  {Object} props The current React component props.
 * @return {Object} Updated React component props.
 */
const mapStateToProps = function (state, ownProps) {
  return {};
};

/**
 * Binds Redux action dispatches to React component props.
 *
 * @private
 *
 * @param  {Function} dispatch Dispatch context.
 * @return {Object} The mapped functions.
 */
const mapDispatchToProps = (dispatch) => {
  return {};
};

// Generate connected/smart component.
const CaptchaContainer = connect(mapStateToProps, mapDispatchToProps)(Captcha);

// Export the connected/smart component as default.
export default CaptchaContainer;
